import { Fragment, useRef } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { cva, cx, VariantProps } from 'class-variance-authority';
import {
  Control,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

export type SelectOption<T extends string | number = string> = {
  id: string;
  label: string;
  value: any;
};
export type FormSelectProps<T extends FieldValues = FieldValues> =
  UseControllerProps<T> &
    VariantProps<typeof SELECT_STYLES> & {
      options: SelectOption[];
      multiple?: boolean;
      allowEmpty?: boolean;
      disabled?: boolean;
      isLoading?: boolean;
      placeholder?: string;
      onChange?: (option: SelectOption) => void;
      inModal?: boolean;
      control: Control<T>;
    };

export const SELECT_STYLES = cva(
  'relative w-full rounded-[10px] leading-[2.25rem] text-[1.5rem] py-[1rem] px-[2.4rem] bg-brand-darker-white focus:bg-brand-white focus:shadow-none focus:ring-0 text-left',
  {
    variants: {
      error: {
        true: 'focus:ring-0 outline outline-2 outline-brand-red focus:outline-brand-red',
        false:
          'outline-hidden focus:outline focus:outline-2 focus:outline-brand-secondary',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
      },
      defaultVariants: {
        error: 'false',
      },
    },
  }
);

function FormSelect<T extends FieldValues>({
  allowEmpty = true,
  options,
  placeholder = '',
  error,
  disabled,
  inModal = false,
  multiple = false,
  ...controllerProps
}: FormSelectProps<T>) {
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController(controllerProps);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (allowEmpty && !!value && !options?.some((o) => o.id === 'empty')) {
    options.unshift({ id: 'empty', label: '', value: '' });
  }
  const selectedOption = options.find((o) => o.value === value);
  const isError = !!(Boolean(errors[controllerProps.name as string]) || error);
  return (
    <Listbox
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      multiple={multiple}
    >
      {({ open }) => {
        return (
          <div className="relative w-full">
            <Listbox.Button
              className={SELECT_STYLES({ error: isError, disabled })}
              ref={inModal ? buttonRef : null}
            >
              <span className="block min-h-[2.25rem] truncate pr-[2.5rem]">
                {multiple
                  ? value
                    ? value.join(', ')
                    : placeholder
                  : selectedOption?.label || placeholder}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-[2rem] flex items-center pr-2">
                <ChevronDownIcon
                  className="h-8 w-8 text-brand-gray"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-2 max-h-96 w-full overflow-auto rounded-[.8rem] bg-brand-white py-1 text-[1.4rem] shadow-xs ring-1 ring-brand-black/5 focus:outline-hidden">
                {options.map((option) => (
                  <Listbox.Option
                    key={option.id}
                    className={({ active }) =>
                      cx(
                        active ? 'bg-brand-secondary-light' : '',
                        'relative flex h-10 select-none items-center pl-10 pr-9'
                      )
                    }
                    value={option.value ?? options[0].value}
                    onClick={() => {
                      // q: wtf is this?
                      // a: when in a modal, this listbox options don't close when clicked
                      // so in order to manually close it, we're calling a click event on the button
                      // after a time out -- it needs to be at the end of the event loop to close the listbox
                      if (inModal)
                        setTimeout(() => {
                          buttonRef.current?.click();
                        }, 0);
                    }}
                  >
                    {({ selected }) => (
                      <>
                        <span className={cx('block truncate')}>
                          {option.label}
                        </span>

                        {selected ? (
                          <span
                            className={cx(
                              'absolute inset-y-0 right-0 flex items-center pr-4'
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        );
      }}
    </Listbox>
  );
}

export default FormSelect;
